import { Box, Button, HStack, IconButton, VStack } from '@chakra-ui/react'
import { AutoResizeTextarea, AutoResizeTextareaProps } from '@src/components/sco/AutoResizeTextarea'
import { DeleteIcon } from '@src/components/sco/icons'
import React, { useCallback, useEffect, useState } from 'react'
import { LuPlus } from 'react-icons/lu'

export type TextareaListFieldProps = {
  itemLabel?: string
  onChange?: (value: string) => void
  value?: string
  valueSeparator?: string
  textareaProps?: AutoResizeTextareaProps
}

export const TextareaListField: React.FC<TextareaListFieldProps> = props => {
  const valueSeparator = props.valueSeparator || '\n\n'

  const splitValue = useCallback(
    (val?: string): string[] => {
      if (val == null || val === '') {
        return ['']
      }
      const parts = val.split(valueSeparator)
      return parts.length === 0 ? [''] : parts
    },
    [valueSeparator]
  )

  const [values, setValues] = useState<string[]>(() => splitValue(props.value))

  // Keep internal state in sync when external value or separator changes
  useEffect(() => {
    setValues(splitValue(props.value))
  }, [props.value, splitValue])

  const joinValues = useCallback(
    (items: string[]) => (props.onChange ? props.onChange(items.join(valueSeparator)) : undefined),
    [props.onChange, valueSeparator]
  )

  const updateItemAtIndex = useCallback(
    (index: number, next: string) => {
      setValues(prev => {
        const copy = [...prev]
        copy[index] = next
        // propagate
        joinValues(copy)
        return copy
      })
    },
    [setValues, joinValues]
  )

  const addItem = useCallback(() => {
    setValues(prev => {
      const next = [...prev, '']
      joinValues(next)
      return next
    })
  }, [setValues, joinValues])

  const removeItemAtIndex = useCallback(
    (index: number) => {
      setValues(prev => {
        const next = prev.filter((_, i) => i !== index)
        joinValues(next)
        return next
      })
    },
    [setValues, joinValues]
  )

  return (
    <VStack align={'stretch'} gap={3} width={'full'}>
      {values.length === 0 && (
        <Box>
          <Button size={'sm'} variant={'outline'} width={'full'} onClick={addItem}>
            <LuPlus />
            {'Add '}
            {props.itemLabel || 'Item'}
          </Button>
        </Box>
      )}
      {values.map((val, idx) => (
        <HStack key={idx} align={'start'} gap={2} width={'full'}>
          <AutoResizeTextarea
            minRows={1}
            value={val}
            width={'full'}
            onChange={e => updateItemAtIndex(idx, e.target.value)}
            {...props.textareaProps}
          />
          <IconButton
            aria-label={'Remove'}
            size={'sm'}
            variant={'ghost'}
            onClick={() => removeItemAtIndex(idx)}
          >
            <DeleteIcon />
          </IconButton>
        </HStack>
      ))}
      {values.length > 0 && (
        <Button size={'sm'} variant={'outline'} width={'full'} onClick={addItem}>
          <LuPlus />
          {'Add '}
          {props.itemLabel || 'Item'}
        </Button>
      )}
    </VStack>
  )
}
